import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Phone, Mail, Calendar, Clock, ArrowRight, Home } from 'lucide-react';

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isReferral, name } = location.state || {};

  const firstName = name ? name.split(' ')[0] : 'there';

  const consultationSteps = [
    {
      id: 'step-1',
      icon: Mail,
      title: 'Check Your Email',
      description: 'You will receive a confirmation email shortly with details about your request.',
      timing: 'Within a few minutes'
    },
    {
      id: 'step-2',
      icon: Phone,
      title: 'We Will Reach Out',
      description: 'A member of our team will contact you to schedule your free consultation.',
      timing: 'Within 1 business day'
    },
    {
      id: 'step-3',
      icon: Calendar,
      title: 'Your Consultation',
      description: 'We will discuss your IT needs, challenges, and how we can help your organization.',
      timing: '30-minute call or video chat'
    }
  ];

  const referralSteps = [
    {
      id: 'ref-step-1',
      icon: Mail,
      title: 'Confirmation Sent',
      description: 'We have received your referral and will follow up with the business you referred.',
      timing: 'Within a few minutes'
    },
    {
      id: 'ref-step-2',
      icon: Phone,
      title: 'We Contact Them',
      description: 'Our team will reach out to the business and offer a free consultation.',
      timing: 'Within 1-2 business days'
    },
    {
      id: 'ref-step-3',
      icon: CheckCircle,
      title: 'You Get Paid',
      description: 'Once they become a client and complete their first month, you receive $300!',
      timing: 'After first month of service'
    }
  ];

  const steps = isReferral ? referralSteps : consultationSteps;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-green-50 via-emerald-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="thank-you-heading">
            {isReferral ? 'Thank You for Your Referral!' : 'Thank You, ' + firstName + '!'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {isReferral 
              ? 'We appreciate you thinking of us. Your referral has been received and we will take great care of them.'
              : 'Your request has been submitted successfully. We are excited to learn more about your organization.'}
          </p>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">What Happens Next</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card key={step.id} className="border-2 border-gray-100 hover:border-blue-200 transition-colors relative">
                <CardContent className="pt-8 pb-6 text-center">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{step.description}</p>
                  
                  {/* Timing Badge */}
                  <div className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full">
                    <Clock className="w-3 h-3 text-gray-500 mr-1" />
                    <span className="text-xs text-gray-600">{step.timing}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-blue-100 bg-blue-50/50">
            <CardContent className="py-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Need Immediate Assistance?</h3>
                <p className="text-gray-600 mb-6">
                  For urgent IT issues or questions, do not hesitate to reach out directly.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => window.location.href = 'tel:+17746447101'}
                    data-testid="thank-you-call-button"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call 774-644-7101
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => window.location.href = 'mailto:info@microsystemsnetworking.com'}
                    data-testid="thank-you-email-button"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Us
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Explore More Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">While You Wait, Explore Our Services</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card 
              className="border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/managed-it')}
            >
              <CardContent className="pt-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Managed IT Services
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Complete IT support for small businesses and nonprofits.
                </p>
                <span className="text-blue-600 text-sm font-medium inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </CardContent>
            </Card>

            <Card 
              className="border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/cybersecurity')}
            >
              <CardContent className="pt-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Cybersecurity
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Protect your business from threats with our security solutions.
                </p>
                <span className="text-blue-600 text-sm font-medium inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </CardContent>
            </Card>

            <Card 
              className="border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/microsoft-365')}
            >
              <CardContent className="pt-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Microsoft 365
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Get the most out of Microsoft 365 with expert administration.
                </p>
                <span className="text-blue-600 text-sm font-medium inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/')}
              data-testid="thank-you-home-button"
            >
              <Home className="w-4 h-4 mr-2" />
              Return to Homepage
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThankYouPage;
